<script>
import Vue from 'vue';
import InfiniteLoading from 'vue-infinite-loading';
import { Select, Option } from 'element-ui';
import debounce from 'lodash/debounce';
import uniqBy from 'lodash/uniqBy';
import InfiniteMixin from '~/mixins/infinite_mixin';
import { startToCurrentPage } from '~/utils/page_utils';
import { NamespacedCache } from '~/services/cache';

Vue.use(Select);
Vue.use(Option);

export default {
    name: 'select-infinite',

    components: {
        InfiniteLoading,
    },

    mixins: [InfiniteMixin],

    model: {
        prop: 'value',
        event: 'change',
    },

    props: {
        value: {
            type: [String, Number, Array],
            default: null,
        },

        placeholder: {
            type: String,
            default: 'Select',
        },

        remoteUrl: {
            type: String,
            default: null,
        },

        remoteMethod: {
            type: Function,
            default() {
                return (params) => new Promise((resolve, reject) => {
                    $.ajax({
                        url: this.remoteUrl,
                        method: 'GET',
                        data: params,
                        global: false,
                    }).done((data) => {
                        resolve(data);
                    }).fail((xhr) => {
                        console.error('An error occurred in ajax select.', xhr);
                        reject(xhr);
                    });
                });
            },
        },

        remoteValueMethod: {
            type: Function,
            default: null,
        },

        loadingText: {
            type: String,
            default: 'Loading...',
        },

        dataPath: {
            type: String,
            default: 'data',
        },

        optionCss: {
            type: String,
            default: null,
        },

        optionKey: {
            type: String,
            default: 'id',
        },

        optionValue: {
            type: String,
            default: 'value',
        },

        optionLabel: {
            type: String,
            default: 'label',
        },

        optionCacheKey: {
            type: String,
            default: null,
        },

        optionsPerPage: {
            type: Number,
            default: 50,
        },

        pagingStrategy: {
            type: String,
            default: 'dt',
            validator(val) {
                return val === 'dt' || val === 'page';
            },
        },

        multiple: {
            type: Boolean,
            default: false,
        },

        multipleLimit: {
            type: Number,
            default: 0,
        },

        clearable: {
            type: Boolean,
            default: true,
        },

        allowCreate: {
            type: Boolean,
            default: false,
        },

        disabled: {
            type: Boolean,
            default: false,
        },

        prependOptions: {
            type: Array,
            default: () => [],
        },

        // This prop can be changed to trigger a reset on options.
        identifier: {
            type: Number,
        },

        clearQueryOnChange: {
            type: Boolean,
            default: true,
        },

        disabledOptions: {
            type: Array,
            default: () => [],
        },

        suggestionSearch: {
            type: Boolean,
            default: false,
        },

        charLimit: {
            type: Boolean,
            default: false,
        },

        limit: {
            type: String,
            default: '',
        },
    },

    data() {
        return {
            options: [],
            valueOptions: [],
            optionCache: null,
            selectedPlaceholder: this.placeholder,
            loading: false,
            isOpen: false,
            changed: false,
            requestCounter: 0,
            modelValue: null,
            previousValue: null,
            search: null,
            isLoadingVal: false,
        };
    },

    computed: {
        queryParams() {
            const params = {};

            if (this.pagingStrategy === 'page') {
                params.page = this.currentPage;
                params.page_size = this.optionsPerPage;
                params.search = this.search;
            } else if (this.pagingStrategy === 'dt') {
                params.start = this.options.length;
                params.length = this.optionsPerPage;
                params.search = { value: this.search };
            }

            return params;
        },

        currentPage() {
            return startToCurrentPage(this.options.length, this.optionsPerPage);
        },

        allOptions() {
            return uniqBy([
              ...this.prependOptions,
              ...this.valueOptions,
              ...this.options
            ], this.optionKey);
        },
    },

    watch: {
        value: {
            handler(new_val, old_val) {
                this.modelValue = new_val;
                this.previousValue = old_val || new_val;
                if (this.isValueCleared(new_val)) {
                    this.clear();
                }
                this.loadUnloadedValues();
            },
            immediate: true,
        },

        search() {
            this.options = [];
            this.valueOptions = [];
            this.infiniteFilterChange();
        },

        isLoadingVal() {
            this.updateRemoteValueLoading();
        },

        identifier() {
            this.options = [];
            this.valueOptions = [];
            this.infiniteFilterChange();
        },
    },

    created() {
        if (this.optionCacheKey) {
            this.optionCache = new NamespacedCache(`select-infinite-${this.optionCacheKey}`, 'local');
        }
    },

    mounted() {
        this.updateRemoteValueLoading();
    },

    methods: {
        loadUnloadedValues() {
            const unloaded = this.getUnloadedValues();

            if (unloaded.length > 0) {
                if (this.hasCachedOptions(unloaded)) {
                    this.loadValueFromCache(unloaded);
                } else if (this.remoteValueMethod) {
                    this.loadRemoteValue(unloaded);
                }
            }
        },

        hasCachedOptions(options) {
            if (this.optionCache === null) {
                return false;
            }

            return this.optionCache.hasItems(options);
        },

        loadValueFromCache(values) {
            if (this.optionCache === null) {
                return;
            }

            this.optionCache.getItems(values).forEach((cachedOption) => {
                this.valueOptions.push(cachedOption);
            });
        },

        getValueAsArray() {
            return Array.isArray(this.value) ? this.value : [this.value];
        },

        getUnloadedValues() {
            const unloaded = [];

            this.getValueAsArray().forEach((val) => {
                if (!val) {
                    return;
                }

                const option = this.getOptionById(val);

                if (!option) {
                    unloaded.push(val);
                }
            });

            return unloaded;
        },

        getOptionById(id) {
            return this.allOptions.find((opt) => opt[this.optionValue] == id);
        },

        loadRemoteValue(values) {
            this.loading = true;
            this.isLoadingVal = true;

            this
            .remoteValueMethod(values)
            .then((response) => {
                this.addValueOptionsFromResponse(response);
            })
            .finally(() => {
                this.loading = false;
                this.isLoadingVal = false;
            });
        },

        updateRemoteValueLoading() {
            this.$nextTick(() => {
                if (!this.$refs.select) return;

                if (this.multiple) {
                    if (!this.isLoadingVal) {
                        this.$refs.select.setSelected();
                    }
                } else if (this.isLoadingVal) {
                    this.$refs.select.selectedLabel = this.loadingText;
                } else {
                    this.$refs.select.setSelected();
                }
            });
        },

        isValueCleared(value) {
            return this.multiple && Array.isArray(value)
                ? value.length === 0
                : (value === undefined || value === null);
        },

        clear() {
            if (this.optionCache) {
                this.optionCache.clear();
            }
            this.options = [];
            this.valueOptions = [];
            this.selectedPlaceholder = this.placeholder;
            this.search = null;
        },

        onInfinite($state) {
            const sequence = ++this.requestCounter;
            this
            .remoteMethod(this.queryParams)
            .then((response) => {
                if (sequence !== this.requestCounter) {
                    return;
                }
                this.addOptionsFromResponse(response);
                this.handleInfinite($state, {
                    responseLength: this.getDataFromResponse(response).length || this.options.length,
                    isComplete: this.getIsCompleteFromResponse(response),
                });
            });
        },

        onSearch(value) {
            // Don't do a search while manually loading value.
            if (this.isLoadingVal) {
                return;
            }

            this.doSearch(value);
        },

        doSearch: debounce(function (value) {
            this.search = value;
        }, 400),

        onVisibleChange(val) {
            if (!this.multiple && this.modelValue) {
                if (val) {
                    this.selectedPlaceholder = this.$refs.select.selectedLabel;
                    this.$refs.select.selectedLabel = '';
                    this.search = null;
                } else {
                    this.selectedPlaceholder = this.placeholder;
                }
            }
            this.isOpen = val;
        },

        onChange(value) {
            let obj;
            if (Array.isArray(value)) {
                obj = [];

                this.options.forEach((option) => {
                    value.forEach((val) => {
                        if (option[this.optionValue] == val) {
                            obj.push(option);

                            if (this.optionCache) {
                                this.optionCache.setItem(val, option);
                            }
                        }
                    });
                });
            } else {
                obj = this.getOptionById(value);
                if (obj && this.optionCache) {
                    this.optionCache.setItem(value, obj);
                }
            }

            this.$emit('change', value, obj);
        },

        onClear() {
            this.clear();
            this.$emit('clear', this.previousValue, this.getOptionById(this.previousValue));
        },

        onRemove(removed) {
            this.$emit('remove-tag', removed.value, this.getOptionById(removed.value));
        },

        getDataFromResponse(response) {
            return response[this.dataPath] || [];
        },

        getIsCompleteFromResponse(response) {
            if (this.pagingStrategy === 'dt' && response.recordsTotal) {
                return this.options.length >= response.recordsTotal;
            } if (this.pagingStrategy === 'page' && response.meta) {
                return response.meta.current_page >= response.meta.total_pages;
            }
            return true;
        },

        addOptionsFromResponse(response) {
            const data = this.getDataFromResponse(response);

            this.options = this.options.concat(data);
        },

        addValueOptionsFromResponse(response) {
            const data = this.getDataFromResponse(response);

            this.valueOptions = this.valueOptions.concat(data);
            const dataIds = [];
            data.forEach((item) => dataIds.push(item[this.optionValue]));
            this.$emit('loaded', dataIds, data);
        },

        isDisabledOption(key) {
            return this.disabledOptions.includes(key);
        },
    },
};
</script>

<template>
    <el-select
        ref="select"
        v-model="modelValue"
        :placeholder="selectedPlaceholder"
        :multiple="multiple"
        :multiple-limit="multipleLimit"
        filterable
        :clearable="clearable"
        remote
        :allow-create="allowCreate"
        :loading="loading"
        :loading-text="loadingText"
        :remote-method="onSearch"
        :disabled="isLoadingVal || disabled"
        @visible-change="onVisibleChange"
        @change="onChange"
        @remove-tag="onRemove"
        @clear="onClear"
        @input="$emit('input', $event)"
    >
        <template v-if="isLoadingVal && multiple">
            <el-option
                v-for="val in getValueAsArray()"
                :key="val"
                :label="loadingText"
                :value="val"
            />
        </template>
        <template v-else-if="optionCss">
            <el-option
                v-for="item in allOptions"
                :key="item[optionKey]"
                :class="item[optionCss]"
                :label="item[optionLabel]"
                :value="item[optionValue]"
                :disabled="isDisabledOption(item[optionKey])"
            >
                <slot :item="item" />
            </el-option>
        </template>
        <template v-else>
            <el-option
                v-for="item in allOptions"
                :key="item[optionKey]"
                :label="item[optionLabel]"
                :value="item[optionValue]"
                :disabled="isDisabledOption(item[optionKey])"
            >
                <slot :item="item" />
            </el-option>
        </template>
        <el-option
            key="loader"
            class="no-hover"
            label="loader"
            value="loader"
            disabled
        >
            <infinite-loading v-if="isOpen" ref="infiniteLoading" @infinite="onInfinite">
                <span v-if="suggestionSearch" slot="no-more" />
                <span v-else slot="no-more">
                    No more options
                </span>
                <span v-if="suggestionSearch" slot="no-results" />
                <span v-else-if="charLimit" slot="no-results">
                    Please enter at least {{ limit }} characters.
                </span>
                <span v-else slot="no-results"> No Results </span>
            </infinite-loading>
        </el-option>
    </el-select>
</template>
